/**
 * Unlinked preview of the real notification settings, used to screenshot
 * the four audiences. It does not read or write preferences.
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { rememberThemeChoice } from '../shared/themePreference';
import { AGENT_MOBILE_TABS, HOMEOWNER_MOBILE_TABS } from '../shared/mobileTabs';
import AppTabBar from '../components/AppTabBar';
import { HOMEOWNER_DESKTOP_TAB_IDS, homeownerTabs } from '../shared/homeownerNav';
import { AgentNotificationSettings, HomeownerNotificationSettings, HOMEOWNER_PREF_DEFAULTS } from '../components/NotificationPreferences';

const PREVIEW_PATH = '/preview/notification-settings';
const BRAND = '#59c4c4';
const BRAND_DEEP = '#0f766e';
const AUDIENCES = ['agent', 'sponsored', 'sponsorless', 'readonly'];

export function isNotificationPreviewPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  return path === PREVIEW_PATH;
}

export function primePreviewTheme(storage, search) {
  if (!storage || typeof storage.setItem !== 'function') return;
  const theme = new URLSearchParams(search || '').get('theme');
  if (theme !== 'dark' && theme !== 'light') return;
  rememberThemeChoice(storage, theme === 'dark');
}

function readAudience(search) {
  const audience = new URLSearchParams(search || '').get('audience') || '';
  return AUDIENCES.indexOf(audience) === -1 ? '' : audience;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  ));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function Wordmark({ isMobile, isDark }) {
  const src = isDark
    ? `${process.env.PUBLIC_URL}/images/logo/pacarion-logo-dark.png`
    : `${process.env.PUBLIC_URL}/images/logo/pacarion-logo-light.png`;
  return (
    <img
      className="top-bar-wordmark"
      src={src}
      alt="Pacarion"
      style={{ height: isMobile ? '33px' : '40px', width: 'auto', maxWidth: '70%', minWidth: 0 }}
    />
  );
}

function AppHeader({ isMobile }) {
  const { colors, isDark } = useTheme();
  return (
    <header style={{
      background: colors.surface,
      color: colors.text,
      borderBottom: `1px solid ${colors.border}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      width: '100%',
      minHeight: isMobile ? '44px' : 'auto',
      padding: isMobile ? '6px 16px' : '1.5rem 3rem',
      position: 'relative',
      zIndex: 2,
    }}
    >
      <Wordmark isMobile={isMobile} isDark={isDark} />
    </header>
  );
}

function AgentSidebar({ colors }) {
  const items = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'profile', label: 'My Profile' },
    { id: 'clients', label: 'Clients' },
    { id: 'vendors', label: 'Vendors' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'projects', label: 'Projects' },
    { id: 'assistant', label: 'Assistant' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'home-history', label: 'Pacarion Home History Report' },
    { id: 'settings', label: 'Settings' },
  ];
  return (
    <nav aria-label="Agent" style={{
      width: '250px',
      flex: '0 0 250px',
      background: colors.surface,
      borderRight: `1px solid ${colors.border}`,
      padding: '2rem 0',
      boxSizing: 'border-box',
    }}
    >
      {items.map((item) => {
        const active = item.id === 'settings';
        return (
          <div key={item.id} style={{
            padding: '1.2rem 2rem',
            color: active ? colors.primary : colors.textSecondary,
            borderLeft: `2px solid ${active ? colors.primary : 'transparent'}`,
            fontSize: '0.9rem',
            fontWeight: 400,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}
          >
            {item.label}
          </div>
        );
      })}
    </nav>
  );
}

function HomeownerDesktopTabs({ colors, isDark }) {
  const active = isDark ? BRAND : BRAND_DEEP;
  const tabs = homeownerTabs(HOMEOWNER_DESKTOP_TAB_IDS);
  return (
    <div style={{
      background: colors.surface,
      borderBottom: `1px solid ${colors.border}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      width: '100%',
    }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', padding: '0 1.25rem' }}>
        {tabs.map((tab) => {
          const selected = tab.id === 'settings';
          return (
            <div key={tab.id} style={{
              padding: '0.85rem 0.65rem',
              color: selected ? active : colors.textSecondary,
              borderBottom: `2px solid ${selected ? active : 'transparent'}`,
              fontSize: '0.8125rem',
              fontWeight: 400,
              letterSpacing: '0.01em',
              whiteSpace: 'nowrap',
            }}
            >
              {tab.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PhoneTabBar({ colors, tabs, activeId, isDark }) {
  return (
    <AppTabBar
      tabs={tabs}
      activeId={activeId}
      colors={colors}
      isDark={isDark}
      fixed={false}
    />
  );
}

export default function NotificationSettingsPreview() {
  const { colors, isDark } = useTheme();
  const isMobile = useIsMobile();
  const audience = readAudience(typeof window !== 'undefined' ? window.location.search : '');
  const role = audience === 'agent' ? 'agent' : 'homeowner';
  const tabs = role === 'agent' ? AGENT_MOBILE_TABS : HOMEOWNER_MOBILE_TABS;
  const activeTab = role === 'agent' ? 'settings' : 'settings';

  return (
    <div data-preview-ready="true" style={{
      minHeight: '100vh',
      background: colors.background,
      color: colors.text,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}
    >
      <AppHeader isMobile={isMobile} />
      {!isMobile && role === 'homeowner' ? <HomeownerDesktopTabs colors={colors} isDark={isDark} /> : null}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {!isMobile && role === 'agent' ? <AgentSidebar colors={colors} /> : null}
        <main style={{ flex: 1, padding: isMobile ? '1rem' : '2rem 3rem', boxSizing: 'border-box' }}>
          {role === 'agent' ? (
            <button type="button" style={{
              background: 'transparent',
              border: 'none',
              color: colors.primary,
              fontSize: '1rem',
              fontWeight: 650,
              padding: '0 0 1rem',
              fontFamily: 'inherit',
            }}
            >
              ‹ Settings
            </button>
          ) : null}
          {audience === 'agent' ? (
            <AgentNotificationSettings profile={{ id: 'preview' }} preview />
          ) : (
            <HomeownerNotificationSettings
              audience={audience || 'sponsorless'}
              preferences={HOMEOWNER_PREF_DEFAULTS}
            />
          )}
        </main>
      </div>
      {isMobile ? <PhoneTabBar colors={colors} tabs={tabs} activeId={activeTab} isDark={isDark} /> : null}
    </div>
  );
}
